import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DMInvitePopup from "../../../../../components/DMInvitePopup/DMInvitePopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeamMembersCard = ({ projectId }) => {
  const [admin, setAdmin] = useState(null);
  const [members, setMembers] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [isDMOpen, setIsDMOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const navigate = useNavigate();

  const extractUsername = (email) => email?.split("@")[0] || "Unknown";
  const getDisplayName = (user) =>
    user.firstName || user.lastName
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : extractUsername(user.email);

  const handleMemberClick = (email) => {
    if (!email) return;
    navigate(`/${extractUsername(email)}`);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await fetch(`${BACKEND_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.email) {
        setCurrentUserEmail(data.email);
        localStorage.setItem("userEmail", data.email);
      }
    };

    const fetchUser = async (email) => {
      const res = await fetch(`${BACKEND_URL}/user/${encodeURIComponent(email)}`);
      const data = await res.json();
      return res.ok ? data : null;
    };

    const fetchTeam = async () => {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/projects/${projectId}/team-all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (res.ok) {
        const fullAdmin = await fetchUser(data.admin.email);
        // backend already groups roles for each member
        const enrichedMembers = await Promise.all(
          data.members.map(async (m) => {
            const full = await fetchUser(m.email);
            return { ...m, ...full };
          })
        );

        setAdmin({ ...data.admin, ...fullAdmin });
        setMembers(enrichedMembers);
      }
    };

    if (!localStorage.getItem("userEmail")) fetchProfile();
    else setCurrentUserEmail(localStorage.getItem("userEmail"));

    fetchTeam();
  }, [projectId]);

  const allMembers = admin ? [admin, ...members] : members;

  return (
    <div
      className="w-96 h-52 bg-white rounded-[20px] shadow-sm flex-shrink-0 flex flex-col p-3 
                 overflow-hidden transition-transform duration-300 hover:-translate-y-2 
                 hover:shadow-xl hover:shadow-green-200 animate-fadeIn"
    >
      {/* Header */}
      <h2 className="text-sm font-semibold text-gray-800 mb-2 px-1 truncate">
        <span className="text-gray-800 font-bold">Team</span>
      </h2>

      {/* Members Grid */}
      {allMembers.length > 0 ? (
        <div className="flex-1 px-2 pr-1 grid grid-cols-4 gap-4 content-start overflow-y-auto">
          {allMembers.map((m, i) => {
            const isAdmin = admin && m.email === admin.email;
            const isCurrentUser =
              m.email?.toLowerCase() === currentUserEmail?.toLowerCase();

            return (
              <div
                key={i}
                className={`flex flex-col items-center cursor-pointer transition-transform duration-200 ${
                  isCurrentUser
                    ? "ring-2 ring-blue-400 rounded-xl p-1 bg-blue-50 hover:scale-105"
                    : "hover:animate-bounceUp"
                }`}
                onClick={() => handleMemberClick(m.email)}
                title={
                  isCurrentUser
                    ? "That's you!"
                    : isAdmin
                    ? "Project Admin"
                    : getDisplayName(m)
                }
              >
                {/* Avatar */}
                <div className="w-10 h-10 bg-zinc-300 rounded-full overflow-hidden flex items-center justify-center">
                  <img
                    src={m.profileImage || "/person.png"}
                    alt={getDisplayName(m)}
                    className="w-full h-full object-cover"
                    onError={(e) => (e.target.src = "/person.png")}
                  />
                </div>

                {/* Name */}
                <p
                  className={`text-[10px] mt-1 max-w-[70px] truncate text-center leading-tight ${
                    isCurrentUser ? "text-blue-700 font-semibold" : "text-gray-700"
                  }`}
                >
                  {isCurrentUser ? "You" : getDisplayName(m)}
                </p>

                {/* Role indicator */}
                <p
                  className={`text-[9px] text-center leading-tight ${
                    isAdmin ? "text-gray-500" : "text-gray-400 italic"
                  }`}
                >
                  {isAdmin ? "(Admin)" : "(Member)"}
                </p>

                {/* Roles (comma-separated) */}
                {m.roles?.length > 0 && (
                  <p className="text-[9px] text-gray-600 text-center leading-tight italic mt-[1px]">
                    {m.roles.join(", ")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-xs italic animate-pulse">
          No team members yet. Invite your crew 🚀
        </div>
      )}

      {/* DM Popup */}
      {isDMOpen && selectedMember && (
        <DMInvitePopup
          applicantEmail={selectedMember.email}
          applicantName={selectedMember.name}
          onClose={() => setIsDMOpen(false)}
          navigate={navigate}
        />
      )}
    </div>
  );
};

export default TeamMembersCard;
